import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import invitationService from '../services/invitationService'
import { Invitation, InvitationStatus } from '../types/invitation'

interface InvitationsState {
  invitations: Invitation[]
  loading: boolean
  error: string | null
  unreadCount: number
}

const initialState: InvitationsState = {
  invitations: [],
  loading: false,
  error: null,
  unreadCount: 0
}

// Async thunks
export const fetchInvitations = createAsyncThunk(
  'invitations/fetchInvitations',
  async () => {
    const response = await invitationService.getInvitations()
    return response
  }
)

export const acceptInvitation = createAsyncThunk(
  'invitations/acceptInvitation',
  async ({
    invitationId,
    countryCode,
    city,
  }: {
    invitationId: string
    countryCode?: string
    city?: string
  }) => {
    const response = await invitationService.acceptInvitation(invitationId, { countryCode, city })
    return { invitationId, response }
  }
)

export const declineInvitation = createAsyncThunk(
  'invitations/declineInvitation',
  async (invitationId: string) => {
    const response = await invitationService.declineInvitation(invitationId)
    return { invitationId, response }
  }
)

export const markInvitationsAsRead = createAsyncThunk(
  'invitations/markInvitationsAsRead',
  async () => {
    const response = await invitationService.markInvitationsAsRead()
    return response
  }
)

const invitationsSlice = createSlice({
  name: 'invitations',
  initialState,
  reducers: {
    updateInvitationStatus: (state, action: PayloadAction<{ id: string; status: InvitationStatus }>) => {
      const invitation = state.invitations.find(inv => inv._id === action.payload.id)
      if (invitation) {
        invitation.status = action.payload.status
      }
    },
    addInvitation: (state, action: PayloadAction<Invitation>) => {
      state.invitations.unshift(action.payload)
      state.unreadCount += 1
    },
    removeInvitation: (state, action: PayloadAction<string>) => {
      state.invitations = state.invitations.filter(inv => inv._id !== action.payload)
    },
    clearError: (state) => {
      state.error = null
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch invitations
      .addCase(fetchInvitations.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchInvitations.fulfilled, (state, action) => {
        state.loading = false
        state.invitations = action.payload
        state.unreadCount = action.payload.filter(inv => inv.status === InvitationStatus.Pending).length
      })
      .addCase(fetchInvitations.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch invitations'
      })
      // Accept invitation
      .addCase(acceptInvitation.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(acceptInvitation.fulfilled, (state, action) => {
        state.loading = false
        const invitation = state.invitations.find(inv => inv._id === action.payload.invitationId)
        if (invitation) {
          invitation.status = InvitationStatus.Accepted
          state.unreadCount = Math.max(0, state.unreadCount - 1)
        }
      })
      .addCase(acceptInvitation.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to accept invitation'
      })
      // Decline invitation
      .addCase(declineInvitation.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(declineInvitation.fulfilled, (state, action) => {
        state.loading = false
        const invitation = state.invitations.find(inv => inv._id === action.payload.invitationId)
        if (invitation) {
          invitation.status = InvitationStatus.Rejected
          state.unreadCount = Math.max(0, state.unreadCount - 1)
        }
      })
      .addCase(declineInvitation.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to decline invitation'
      })
      // Mark as read
      .addCase(markInvitationsAsRead.fulfilled, (state) => {
        state.unreadCount = 0
      })
  }
})

export const { updateInvitationStatus, addInvitation, removeInvitation, clearError } = invitationsSlice.actions
export default invitationsSlice.reducer
